import { ActionType } from '../../../core/redux/actionType';
import { Endpoints } from '../../../core/api/endpoints';
import { makeRsaaTypes, makeApiAction, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { AppState } from '../../../core/redux/appState';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';
import * as R from 'ramda';
import { Rater } from './models';
import { Map } from '../../../language/models';
import { Str } from '../../../localization/translationKeys';


const KEY_ACTION = ActionType.UndeliveredEmailsRequest;
const ACTIONS = [
  ActionType.UndeliveredEmailsRequest,
  ActionType.UndeliveredEmailsResponse,
  ActionType.UndeliveredEmailsError,
];


type Payload = string[];


type Meta = {
  projectId: number;
}


/**
 * Dispatch this action to retrieve undelivered emails
 *
 * @param projectId - the database Id for the project for which we're retrieving raters
 * @param participantId - the database Id for the specific project participant whose raters we're retrieving
 */
export const undeliveredEmailsAction = (projectId: number, participantId: number) => makeApiAction<Payload, Meta>(
  {
    method: 'POST',
    endpoint: Endpoints.UndeliveredEmails,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      projectId: projectId,
      participantId: participantId,
    }),
    types: makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const raters         = state.projectRaters?.[action.meta.projectId] || {};
  const undeliveredIds = R.filter(key => payload!.includes(raters[key].email.toLowerCase()), Object.keys(raters));
  const undelivered    = R.pick(undeliveredIds, raters) as Map<Rater>;
  const marked         = Object.values(undelivered).reduce(
    (map, rater) => R.assoc(
      `${rater.raterId}`,
      R.mergeRight(rater, { undeliveredEmail: true, statusMessageKey: Str.Undelivered }),
      map,
    ),
    {},
  );

  const withUndeliveredMarked = R.mergeRight(raters, marked);
  const withUpdatedRaters = R.assocPath(
    ['projectRaters', `${action.meta.projectId}`], withUndeliveredMarked,
    successState,
  );

  return R.assoc('undeliveredEmails', payload || [], withUpdatedRaters);
};

export const undeliveredEmailsReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
